/**
 * The certificado's document-conformity observations (ADR 0012), as rows for the
 * «Datos extraídos» card. They travel in `extracted_fields.certificado.conformidad`
 * ONLY when the model made them: records read before the observations existed
 * (extract-v1) and degraded reads carry no block, and render no rows.
 */

import {
  CERT_ANOMALIA_LABEL,
  CERT_COLOR_FOLIO_LABEL,
  CERT_COLOR_LABEL,
  CERT_ESTADO_QR_LABEL,
  CERT_FORMA_CAPTURA_LABEL,
  CERT_SELLO_FOTO_LABEL,
  CERT_TIPO_DOCUMENTO_LABEL,
  labelOrCode,
  OBSERVACION_LABEL,
} from "../../api/labels";

export type ConformidadRow = { label: string; text: string; muted: boolean };

const NOT_OBSERVED = "no observado";
const NONE_SEEN = "ninguna";

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function coded(map: Record<string, string>, value: unknown): Pick<ConformidadRow, "text" | "muted"> {
  if (typeof value !== "string" || value === "") return { text: NOT_OBSERVED, muted: true };
  return { text: labelOrCode(map, value), muted: value === "NO_DETERMINABLE" };
}

/**
 * `anomalias` is an array, not a coded scalar: `null`/not observed is muted,
 * `[]` means "looked, saw none" (a definite, non-muted finding), and a
 * non-empty list renders as a comma-joined Spanish list (unknown codes fall
 * back to the raw code, same as `coded`).
 */
function anomaliasText(value: unknown): Pick<ConformidadRow, "text" | "muted"> {
  if (!Array.isArray(value)) return { text: NOT_OBSERVED, muted: true };
  if (value.length === 0) return { text: NONE_SEEN, muted: false };
  return {
    text: value.map((code) => labelOrCode(CERT_ANOMALIA_LABEL, String(code))).join(", "),
    muted: false,
  };
}

export function conformidadRows(certificado: unknown): ConformidadRow[] {
  const conformidad = asRecord(asRecord(certificado)?.conformidad);
  if (!conformidad) return [];
  const titulo = conformidad.titulo_literal;
  return [
    { label: "Documento observado", ...coded(CERT_TIPO_DOCUMENTO_LABEL, conformidad.tipo_documento) },
    typeof titulo === "string" && titulo.trim() !== ""
      ? { label: "Título leído", text: titulo, muted: false }
      : { label: "Título leído", text: NOT_OBSERVED, muted: true },
    { label: "Documento completo", ...coded(OBSERVACION_LABEL, conformidad.documento_completo) },
    { label: "Escudo nacional", ...coded(OBSERVACION_LABEL, conformidad.escudo_nacional) },
    { label: "Logo institucional", ...coded(OBSERVACION_LABEL, conformidad.logo_institucional) },
    { label: "Sellos oficiales", ...coded(OBSERVACION_LABEL, conformidad.sellos_oficiales) },
    { label: "Firmas o sello digital", ...coded(OBSERVACION_LABEL, conformidad.firmas_o_sello_digital) },
    { label: "Foto del alumno", ...coded(OBSERVACION_LABEL, conformidad.foto_alumno) },
    { label: "Fondo de seguridad", ...coded(OBSERVACION_LABEL, conformidad.fondo_seguridad) },
    { label: "Forma de captura", ...coded(CERT_FORMA_CAPTURA_LABEL, conformidad.forma_captura) },
    { label: "Anomalías", ...anomaliasText(conformidad.anomalias) },
    { label: "Color", ...coded(CERT_COLOR_LABEL, conformidad.color) },
    { label: "Color del folio", ...coded(CERT_COLOR_FOLIO_LABEL, conformidad.color_folio) },
    { label: "Sello de la foto", ...coded(CERT_SELLO_FOTO_LABEL, conformidad.sello_foto) },
    { label: "Estado del QR", ...coded(CERT_ESTADO_QR_LABEL, conformidad.estado_qr) },
  ];
}
